import styles from "./messenger-frame.module.css";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Flex, Spin, Typography } from "antd";

import {
  clearCachedMessengerBootstrap,
  createMessengerBootstrap,
  getCachedMessengerBootstrap,
} from "@/api/messenger";
import { messengerOriginsMatch } from "@/utils/messengerOrigin";

const MESSAGE_TYPE = "teamtools-messenger-bootstrap";
const READY_TYPE = "teamtools-messenger-ready";
const UNREAD_TYPE = "teamtools-messenger-unread";
const CONTACTS_CHANGED_TYPE = "teamtools-messenger-contacts-changed";
const AUTHENTICATED_TYPE = "teamtools-messenger-authenticated";
const RECOVER_SESSION_TYPE = "teamtools-messenger-recover-session";
const RECOVERY_FAILED_TYPE = "teamtools-messenger-recovery-failed";
const HANDOFF_TO_TAB_TYPE = "teamtools-messenger-handoff-to-tab";
const HANDOFF_COMPLETE_TYPE = "teamtools-messenger-handoff-complete";
const RESUME_EMBED_TYPE = "teamtools-messenger-resume-embed";
const PANEL_STATE_TYPE = "teamtools-messenger-panel-state";
const MAX_BOOTSTRAP_RECOVERY_ATTEMPTS = 3;
const RECOVERY_REQUEST_COOLDOWN_MS = 4000;

const isAuthRecoveryReason = (reason) => {
  if (!reason || typeof reason !== "string") {
    return false;
  }

  return (
    reason.includes("auth-token") ||
    reason.includes("session-logged-out") ||
    reason.includes("token-expired") ||
    reason.includes("http-401") ||
    reason.includes("M_UNKNOWN_TOKEN") ||
    reason.includes("load-session-failed")
  );
};

const isSyncRecoveryReason = (reason) => {
  if (!reason || typeof reason !== "string") {
    return false;
  }

  return reason.includes("sync-error");
};

const isRecoverableReason = (reason) =>
  isAuthRecoveryReason(reason) || isSyncRecoveryReason(reason);

const resolveMessengerApiBaseUrl = () => {
  try {
    const currentUrl = new URL(window.location.origin);

    if (currentUrl.hostname === "localhost" && currentUrl.port === "3000") {
      return "http://localhost:6482";
    }
  } catch (error) {
    // Fallback below keeps existing behavior if URL parsing fails.
  }

  return window.location.origin;
};

const getBootstrapExpiresAtMs = (nextBootstrap) => {
  if (!nextBootstrap?.expires_at) {
    return null;
  }
  const ts = new Date(nextBootstrap.expires_at).getTime();
  return Number.isNaN(ts) ? null : ts;
};

const isBootstrapExpired = (nextBootstrap) => {
  const expiresAtMs = getBootstrapExpiresAtMs(nextBootstrap);
  if (!expiresAtMs) {
    return true;
  }

  return expiresAtMs <= Date.now() + 10 * 1000;
};

const MessengerFrame = ({
  open,
  config,
  onUnreadStateChange,
  delegatedToTab = false,
  delegationChecked = true,
  handoffRequest = 0,
  onHandoffComplete,
  onReopenExternalTab,
  onContinueHere,
  takeoverPending = false,
  ownerToken = null,
}) => {
  const iframeRef = useRef(null);
  const bootstrapRecoveryAttemptRef = useRef(0);
  const lastRecoveryRequestAtRef = useRef(0);
  const lastPostedLaunchTokenRef = useRef(null);

  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [bootstrap, setBootstrap] = useState(() => getCachedMessengerBootstrap());
  const [iframeReady, setIframeReady] = useState(false);
  const [iframeAuthenticated, setIframeAuthenticated] = useState(false);
  const [iframeNonce, setIframeNonce] = useState(0);

  const iframeUrl = config?.messenger_iframe_url || "";

  const targetOrigin = useMemo(() => {
    if (config?.messenger_client_origin) {
      return config.messenger_client_origin;
    }

    try {
      return new URL(iframeUrl).origin;
    } catch (error) {
      return "*";
    }
  }, [config?.messenger_client_origin, iframeUrl]);

  const iframeSrc = useMemo(() => {
    if (!iframeUrl) {
      return "";
    }

    try {
      const nextUrl = new URL(iframeUrl);

      // Element Web checks only the presence of `ttv`, not its value.
      // Keep it stable to avoid iframe remount when bootstrap becomes ready.
      nextUrl.searchParams.set("ttv", "initial");

      return nextUrl.toString();
    } catch (error) {
      const separator = iframeUrl.includes("?") ? "&" : "?";
      return `${iframeUrl}${separator}ttv=${encodeURIComponent("initial")}`;
    }
  }, [iframeUrl]);

  const embedBlocked = delegatedToTab || !delegationChecked;

  const getIframeContentWindow = useCallback(
    () => iframeRef.current?.contentWindow ?? null,
    []
  );

  const postMessageToIframe = useCallback(
    (message) => {
      const iframeWindow = getIframeContentWindow();
      if (!iframeWindow) {
        return false;
      }

      iframeWindow.postMessage(message, targetOrigin);
      return true;
    },
    [getIframeContentWindow, targetOrigin]
  );

  const loadBootstrap = useCallback(async (forceRefresh = false) => {
    const cachedBootstrap = forceRefresh ? null : getCachedMessengerBootstrap();
    if (cachedBootstrap && !isBootstrapExpired(cachedBootstrap)) {
      setError("");
      setBootstrap(cachedBootstrap);
      return cachedBootstrap;
    }

    setPending(true);
    setError("");

    try {
      const nextBootstrap = await createMessengerBootstrap();
      setBootstrap(nextBootstrap);
      return nextBootstrap;
    } catch {
      setBootstrap(null);
      setError("Что-то пошло не так...");
      return null;
    } finally {
      setPending(false);
    }
  }, []);

  const postBootstrapToIframe = useCallback(
    async (forceRefresh = false) => {
      if (!getIframeContentWindow()) {
        return false;
      }

      let nextBootstrap = bootstrap;
      if (forceRefresh || !nextBootstrap || isBootstrapExpired(nextBootstrap)) {
        nextBootstrap = await loadBootstrap(forceRefresh);
      }
      if (!nextBootstrap || !getIframeContentWindow()) {
        return false;
      }

      if (lastPostedLaunchTokenRef.current === nextBootstrap.launch_token) {
        return true;
      }

      lastPostedLaunchTokenRef.current = nextBootstrap.launch_token;
      clearCachedMessengerBootstrap();
      return postMessageToIframe({
        type: MESSAGE_TYPE,
        payload: {
          launchToken: nextBootstrap.launch_token,
          apiBaseUrl: resolveMessengerApiBaseUrl(),
          title: config?.messenger_title || "Мессенджер",
          ownerToken,
        },
      });
    },
    [
      bootstrap,
      config?.messenger_title,
      getIframeContentWindow,
      loadBootstrap,
      ownerToken,
      postMessageToIframe,
    ]
  );

  const requestBootstrapRecovery = useCallback(async () => {
    const now = Date.now();
    if (now - lastRecoveryRequestAtRef.current < RECOVERY_REQUEST_COOLDOWN_MS) {
      return;
    }
    lastRecoveryRequestAtRef.current = now;

    if (bootstrapRecoveryAttemptRef.current >= MAX_BOOTSTRAP_RECOVERY_ATTEMPTS) {
      setError("Что-то пошло не так...");
      return;
    }

    bootstrapRecoveryAttemptRef.current += 1;
    lastPostedLaunchTokenRef.current = null;
    await postBootstrapToIframe(true);
  }, [postBootstrapToIframe]);

  const postContactsChangedToIframe = useCallback(() => {
    postMessageToIframe({
      type: CONTACTS_CHANGED_TYPE,
    });
  }, [postMessageToIframe]);

  const postHandoffToIframe = useCallback(() => {
    const iframeWindow = getIframeContentWindow();
    if (!iframeWindow) {
      onHandoffComplete?.(false);
      return;
    }

    let completed = false;
    const finish = (result) => {
      if (completed) {
        return;
      }
      completed = true;
      window.removeEventListener("message", onMessage);
      window.clearTimeout(timeoutId);
      onHandoffComplete?.(result);
    };

    const onMessage = (event) => {
      if (targetOrigin !== "*" && event.origin !== targetOrigin) {
        return;
      }

      if (event.data?.type === HANDOFF_COMPLETE_TYPE) {
        finish(true);
      }
    };

    window.addEventListener("message", onMessage);
    iframeWindow.postMessage({ type: HANDOFF_TO_TAB_TYPE }, targetOrigin);

    const timeoutId = window.setTimeout(() => finish(false), 5000);
  }, [getIframeContentWindow, onHandoffComplete, targetOrigin]);

  useEffect(() => {
    if (!handoffRequest) {
      return;
    }

    if (delegatedToTab) {
      onHandoffComplete?.(true);
      return;
    }

    postHandoffToIframe();
  }, [delegatedToTab, handoffRequest, onHandoffComplete, postHandoffToIframe]);

  useEffect(() => {
    if (delegatedToTab || !open) {
      return;
    }

    postMessageToIframe({ type: RESUME_EMBED_TYPE });
  }, [delegatedToTab, open, postMessageToIframe]);

  useEffect(() => {
    if (embedBlocked) {
      return undefined;
    }

    postMessageToIframe({
      type: PANEL_STATE_TYPE,
      payload: { open },
    });
  }, [embedBlocked, open, postMessageToIframe]);

  useEffect(() => {
    if (embedBlocked) {
      return undefined;
    }

    const nudgeIframeResume = () => {
      if (document.visibilityState !== "visible") {
        return;
      }

      postMessageToIframe({ type: RESUME_EMBED_TYPE });
    };

    document.addEventListener("visibilitychange", nudgeIframeResume);
    window.addEventListener("pageshow", nudgeIframeResume);
    window.addEventListener("online", nudgeIframeResume);

    return () => {
      document.removeEventListener("visibilitychange", nudgeIframeResume);
      window.removeEventListener("pageshow", nudgeIframeResume);
      window.removeEventListener("online", nudgeIframeResume);
    };
  }, [embedBlocked, postMessageToIframe]);

  useEffect(() => {
    if (!config?.messenger_enabled || !iframeUrl || pending || bootstrap || embedBlocked) {
      return;
    }

    loadBootstrap();
  }, [config?.messenger_enabled, embedBlocked, iframeUrl, pending, bootstrap, loadBootstrap]);

  useEffect(() => {
    if (!iframeReady || !bootstrap || embedBlocked) {
      return;
    }

    void postBootstrapToIframe(false);
  }, [bootstrap, embedBlocked, iframeReady, postBootstrapToIframe]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (
        targetOrigin !== "*" &&
        !messengerOriginsMatch(targetOrigin, event.origin)
      ) {
        return;
      }

      if (event.data?.type === READY_TYPE) {
        setIframeReady(false);
        setIframeAuthenticated(false);
        setError("");
        // Iframe reload (e.g. after token recovery) invalidates the previous launch token.
        // Always fetch and post a fresh bootstrap instead of reusing a consumed token.
        lastPostedLaunchTokenRef.current = null;
        void postBootstrapToIframe(true).then((posted) => {
          if (posted) {
            setIframeReady(true);
          }
        });
        return;
      }

      if (event.data?.type === AUTHENTICATED_TYPE) {
        bootstrapRecoveryAttemptRef.current = 0;
        setError("");
        setIframeAuthenticated(true);
        return;
      }

      if (event.data?.type === RECOVER_SESSION_TYPE) {
        const reason = event.data?.payload?.reason;
        if (iframeAuthenticated && !isRecoverableReason(reason)) {
          return;
        }
        if (iframeAuthenticated && isAuthRecoveryReason(reason)) {
          setIframeAuthenticated(false);
          lastPostedLaunchTokenRef.current = null;
        }
        void requestBootstrapRecovery();
        return;
      }

      if (event.data?.type === RECOVERY_FAILED_TYPE) {
        const reason = event.data?.payload?.reason;
        if (iframeAuthenticated && !isRecoverableReason(reason)) {
          return;
        }
        if (iframeAuthenticated && isAuthRecoveryReason(reason)) {
          setIframeAuthenticated(false);
          lastPostedLaunchTokenRef.current = null;
        }
        void requestBootstrapRecovery();
        return;
      }

      if (event.data?.type === UNREAD_TYPE) {
        onUnreadStateChange?.(Boolean(event.data?.payload?.hasUnread));
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [
    iframeAuthenticated,
    onUnreadStateChange,
    postBootstrapToIframe,
    requestBootstrapRecovery,
    targetOrigin,
  ]);

  useEffect(() => {
    const handleContactsChanged = () => {
      postContactsChangedToIframe();
    };

    window.addEventListener(CONTACTS_CHANGED_TYPE, handleContactsChanged);

    return () => {
      window.removeEventListener(CONTACTS_CHANGED_TYPE, handleContactsChanged);
    };
  }, [postContactsChangedToIframe]);

  useEffect(() => {
    setIframeReady(false);
    setIframeAuthenticated(false);
    lastPostedLaunchTokenRef.current = null;
    bootstrapRecoveryAttemptRef.current = 0;
    setIframeNonce((value) => value + 1);
  }, [iframeUrl]);

  const reloadMessengerIframe = useCallback(() => {
    setError("");
    setBootstrap(null);
    setIframeReady(false);
    setIframeAuthenticated(false);
    bootstrapRecoveryAttemptRef.current = 0;
    lastRecoveryRequestAtRef.current = 0;
    lastPostedLaunchTokenRef.current = null;
    setIframeNonce((value) => value + 1);
    void loadBootstrap(true);
  }, [loadBootstrap]);

  const showSessionOverlay = Boolean(
    iframeUrl &&
      !error &&
      !embedBlocked &&
      (pending || (open && !iframeAuthenticated))
  );

  return (
    <div className={styles.frameBody}>
      <div className={styles.iframeWrap} style={{ position: "relative" }}>
        {!iframeUrl ? (
          <div className={styles.emptyState}>
            URL embedded-клиента не настроен. Укажите `messenger_iframe_url`
            в site config.
          </div>
        ) : delegatedToTab ? (
          <Flex
            vertical
            align="center"
            justify="center"
            gap={12}
            className={styles.emptyState}
          >
            <Typography.Text strong>
              Мессенджер открыт в отдельной вкладке.
            </Typography.Text>
            <Typography.Text className={styles.hint}>
              Если переключение не сработало автоматически, выберите действие:
            </Typography.Text>
            <Flex gap={8} wrap align="center" justify="center">
              <Button
                type="primary"
                onClick={onReopenExternalTab}
                loading={takeoverPending}
              >
                Открыть заново
              </Button>
              <Button onClick={onContinueHere} loading={takeoverPending}>
                Закрыть старую вкладку и продолжить здесь
              </Button>
            </Flex>
          </Flex>
        ) : !delegationChecked ? (
          <Flex
            vertical
            align="center"
            justify="center"
            className={styles.emptyState}
          >
            <Spin />
            <Typography.Text strong>Проверяем мессенджер...</Typography.Text>
          </Flex>
        ) : (
          <>
            <iframe
              key={`${iframeUrl}:${iframeNonce}`}
              ref={iframeRef}
              title={config?.messenger_title || "Мессенджер"}
              src={iframeSrc}
              className={styles.iframe}
              allow="camera; microphone; display-capture; autoplay"
            />

            {error ? (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#ffffff",
                  zIndex: 11,
                }}
              >
                <Flex vertical align="center" justify="center" gap={12}>
                  <Typography.Text strong>{error}</Typography.Text>
                  <Button type="primary" onClick={reloadMessengerIframe}>
                    Перезагрузить мессенджер
                  </Button>
                </Flex>
              </div>
            ) : null}

            {showSessionOverlay ? (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#ffffff",
                  zIndex: 10,
                }}
              >
                <Flex
                  vertical
                  align="center"
                  justify="center"
                  className={styles.emptyState}
                >
                  <Spin />
                  <Typography.Text strong>
                    Подключаем мессенджер...
                  </Typography.Text>
                  <Typography.Text className={styles.hint}>
                    Подождите, мессенджер откроется автоматически.
                  </Typography.Text>
                </Flex>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default MessengerFrame;
